import React, { useContext, JSX } from 'react';
import { ChatState, ChatTogglerShape } from '@enuan/chat__utils';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { styled } from '@mui/material/styles';

import { ReactComponent as TogglerShape } from '@/assets/avatar_base.svg';
import { configurationContext } from '../../contexts/configuration';
import { chatTogglerMessages } from './chatToggler.i18n';
import { ITogglerButtonBaseProps } from './TogglerButton.types';

/**
 * Renders the toggler button's outer shape (custom image, balloon SVG,
 * or plain round div) based on the current chat configuration.
 */
export const TogglerButtonShape = ({ onClick }: ITogglerButtonBaseProps) => {
	const {
		toggler_button: {
			shape_active,
			shape_inactive,
			custom_shape_active,
			custom_shape_inactive,
		} = {},
		state,
	} = useContext(configurationContext);

	const isActive = state !== ChatState.OFF && !!state;
	const intl = useIntl();
	const title = isActive
		? intl.formatMessage(chatTogglerMessages.tooltip_active)
		: intl.formatMessage(chatTogglerMessages.tooltip_inactive);

	const shape = isActive ? shape_active : shape_inactive;
	const customSrc =
		shape === ChatTogglerShape.CUSTOM
			? isActive
				? custom_shape_active
				: custom_shape_inactive
			: undefined;

	// Shape explicitly disabled — render nothing so only the icon (if any) is visible.
	if (shape === ChatTogglerShape.NONE) return null;

	if (customSrc) {
		return <ShapeCustom src={customSrc} onClick={onClick} title={title} />;
	}

	if (shape === ChatTogglerShape.BALLOON) {
		return <ShapeBalloon onClick={onClick} title={title} />;
	}

	return <ShapeRound onClick={onClick} title={title} />;
};

// ── accessories (in order of appearance) ────────────────────────────

const ShapeCustom = ({ title, ...props }: JSX.IntrinsicElements['img']) => (
	<img
		className={classNames('toggler-button__shape')}
		alt={title}
		title={title}
		{...props}
	/>
);

const ShapeBalloon = styled(
	({
		className,
		...props
	}: React.SVGProps<SVGSVGElement> & { title?: string }) => (
		<div className="toggler-button__shape">
			<TogglerShape
				className={classNames('toggler-button__shape-svg', className)}
				{...props}
			/>
		</div>
	),
)({});

const ShapeRound = (props: JSX.IntrinsicElements['div']) => (
	<div className={classNames('toggler-button__shape')} {...props} />
);
